/**
 * Top-of-app society switcher (MyGate model). Lists the signed-in person's
 * approved society memberships (`GET /api/me/societies`) and lets them pick which
 * one the app is scoped to. The choice is written to lib/active-society, which
 * the X-Bero-Society coordinator attaches to every request — so switching
 * re-scopes the whole app (visitors, notices, payments) to that society.
 *
 * A single-society member sees just their society name (no dropdown affordance);
 * the picker only opens when there are two or more. Renders nothing until at
 * least one approved membership loads.
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useFetch } from '@/features/community/lib';
import { getActiveSociety, setActiveSociety, useActiveSociety } from '@/lib/active-society';
import { api } from '@/lib/client';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Hairline, Sheet } from '@/ui';

type MySociety = { societyId: number; societyName?: string; city?: string; flat?: { code: string } };

async function fetchMine(): Promise<{ items: MySociety[]; activeSocietyId: number | null }> {
  const body = await api.get<{ items: MySociety[]; activeSocietyId: number | null }>('/api/me/societies');
  return { items: Array.isArray(body.items) ? body.items : [], activeSocietyId: body.activeSocietyId ?? null };
}

export function SocietySwitcher({ compact, inline }: { compact?: boolean; inline?: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  const active = useActiveSociety();
  const { data } = useFetch(fetchMine, []);
  const [open, setOpen] = React.useState(false);
  const items = data?.items ?? [];

  if (items.length === 0) return null;

  // The active row: the user's stored choice, else the server's resolved default.
  const activeId = active ?? data?.activeSocietyId ?? items[0].societyId;
  const activeRow = items.find((s) => s.societyId === activeId) ?? items[0];
  // Always tappable — even a single-society member opens the sheet, where they
  // can add another flat (MyGate model). The chevron always shows the affordance.

  const pick = (id: number) => {
    setOpen(false);
    if (id === activeId) return;
    setActiveSociety(id);
    // Re-scope the WHOLE app to the new society. The active choice persists to
    // localStorage, so on web a hard navigation to /home is the reliable way to
    // make every screen refetch with the new X-Bero-Society header (a plain
    // router.replace to the current route doesn't remount / refetch). Native
    // remounts on a router replace.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.assign('/home');
    } else {
      router.replace('/home');
    }
  };

  return (
    <>
      {inline ? (
        // Single-line trigger for the mobile top strip (BrandBar): society name +
        // chevron on one row, name truncates. Flat/city move into the sheet.
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('sw_switch_society')}
          onPress={() => setOpen(true)}
          style={({ pressed }) => ({
            flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 4,
            paddingVertical: 6, paddingHorizontal: space.s2, borderRadius: 8,
            backgroundColor: pressed ? colors.canvas1 : 'transparent',
          })}
        >
          <AppText variant="bodyMd" weight="600" numberOfLines={1} style={{ flexShrink: 1 }}>
            {activeRow.societyName ?? t('sw_your_society')}
          </AppText>
          <View style={{ transform: [{ rotate: '90deg' }] }}>
            <Icon name="chevron-right" size={15} color={colors.textSecondary} />
          </View>
        </Pressable>
      ) : (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('sw_switch_society')}
          onPress={() => setOpen(true)}
          style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
            flexDirection: 'row', alignItems: 'center', gap: space.s2,
            paddingVertical: 8, paddingHorizontal: compact ? space.s2 : space.s3, borderRadius: 8,
            backgroundColor: hovered || pressed ? colors.canvas1 : 'transparent',
          })}
        >
          <View style={{ flex: 1, minWidth: 0 }}>
            <AppText variant="bodyMd" weight="600" numberOfLines={1}>
              {activeRow.societyName ?? t('sw_your_society')}
            </AppText>
            {activeRow.flat?.code ? (
              <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>{activeRow.flat.code}</AppText>
            ) : activeRow.city ? (
              <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>{activeRow.city}</AppText>
            ) : null}
          </View>
          <View style={{ transform: [{ rotate: '90deg' }] }}>
            <Icon name="chevron-right" size={16} color={colors.textSecondary} />
          </View>
        </Pressable>
      )}

      <Sheet visible={open} onClose={() => setOpen(false)}>
        <AppText variant="headingSm" weight="600" style={{ marginBottom: space.s3 }}>
          {t('sw_switch_society')}
        </AppText>
        {items.map((s, i) => {
          const selected = s.societyId === activeId;
          return (
            <View key={s.societyId}>
              {i > 0 ? <Hairline /> : null}
              <Pressable
                accessibilityRole="button"
                onPress={() => pick(s.societyId)}
                style={({ pressed }) => ({
                  flexDirection: 'row', alignItems: 'center', gap: space.s3,
                  paddingVertical: 14, paddingHorizontal: space.s2,
                  backgroundColor: pressed ? colors.canvas1 : 'transparent',
                })}
              >
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight={selected ? '600' : '400'} numberOfLines={1}>
                    {s.societyName ?? t('sw_your_society')}
                  </AppText>
                  <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>
                    {s.flat?.code ? s.flat.code : s.city ?? ''}
                  </AppText>
                </View>
                {selected ? <Icon name="check" size={18} color={colors.primary600} /> : null}
              </Pressable>
            </View>
          );
        })}
        <Hairline />
        <Pressable
          accessibilityRole="button"
          onPress={() => { setOpen(false); router.push('/account/manage-flats'); }}
          style={({ pressed }) => ({
            flexDirection: 'row', alignItems: 'center', gap: space.s2,
            paddingVertical: 14, paddingHorizontal: space.s2,
            backgroundColor: pressed ? colors.canvas1 : 'transparent',
          })}
        >
          <Icon name="plus" size={18} color={colors.primary600} />
          <AppText variant="bodyMd" weight="500" tone="brand">{t('sw_add_flat')}</AppText>
        </Pressable>
      </Sheet>
    </>
  );
}
